import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Select, Textarea } from "@/components/ui/field";
import type {
  FormField,
  IntakeForm,
  LocaleCode,
  LocalizedText,
} from "@/shared/types";
import { recaptchaToken } from "@/lib/recaptcha";

function text(value: LocalizedText, locale: LocaleCode) {
  return value[locale] || value["en-US"];
}

function Field({ field, locale }: { field: FormField; locale: LocaleCode }) {
  const id = `field-${field.id}`;
  if (field.type === "checkbox") {
    return (
      <label className="flex cursor-pointer items-start gap-3 rounded-lg border p-4">
        <input
          id={id}
          name={field.id}
          type="checkbox"
          className="mt-1 h-4 w-4 accent-primary"
        />
        <span className="text-sm font-medium leading-6">
          {text(field.label, locale)}
        </span>
      </label>
    );
  }
  if (field.type === "textarea") {
    return <Textarea id={id} name={field.id} required={field.required} />;
  }
  if (field.type === "select" || field.type === "radio") {
    return (
      <Select id={id} name={field.id} required={field.required} defaultValue="">
        <option value="" disabled>
          —
        </option>
        {field.options?.map((option) => (
          <option key={option.value} value={option.value}>
            {text(option.label, locale)}
          </option>
        ))}
      </Select>
    );
  }
  return (
    <Input
      id={id}
      name={field.id}
      required={field.required}
      type={field.type === "phone" ? "tel" : field.type}
      min={field.type === "number" ? 0 : undefined}
    />
  );
}

export function IntakePage() {
  const { t, i18n } = useTranslation();
  const locale: LocaleCode = i18n.language.startsWith("es") ? "es-US" : "en-US";
  const [form, setForm] = useState<IntakeForm | null>(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");

  useEffect(() => {
    api<IntakeForm>("/public/forms/neighbor-signup")
      .then(setForm)
      .catch(() => setError(t("form.error")));
  }, [t]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form) return;
    setError("");
    setSubmitting(true);
    const data = new FormData(event.currentTarget);
    const answers: Record<string, unknown> = {};
    for (const field of form.fields) {
      const value =
        field.type === "checkbox"
          ? data.get(field.id) === "on"
          : data.get(field.id);
      answers[field.id] =
        field.type === "number" && value ? Number(value) : value;
    }
    try {
      const protectionToken = await recaptchaToken("form_submission");
      const response = await api<{ message: string }>(
        `/public/forms/${form.slug}/submissions`,
        {
          method: "POST",
          body: JSON.stringify({
            locale,
            answers,
            recaptcha_token: protectionToken,
          }),
        },
      );
      setSuccess(response.message);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("form.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-20">
        <Card className="border-primary/20 shadow-soft">
          <CardContent className="flex flex-col items-center p-10 text-center">
            <CheckCircle2 className="text-primary" size={52} />
            <h1 className="mt-6 text-3xl font-bold">{success}</h1>
            <Button asChild variant="outline" className="mt-8">
              <Link to="/">{t("common.back")}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-5 py-12 sm:py-16">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary"
      >
        <ChevronLeft size={16} />
        {t("common.back")}
      </Link>
      <Card className="overflow-hidden shadow-soft">
        <CardHeader className="border-b bg-primary p-7 text-primary-foreground sm:p-10">
          <CardTitle className="text-3xl sm:text-4xl">
            {form ? text(form.title, locale) : t("form.loading")}
          </CardTitle>
          {form && (
            <p className="mt-3 max-w-xl text-primary-foreground/80">
              {text(form.description, locale)}
            </p>
          )}
        </CardHeader>
        <CardContent className="p-7 sm:p-10">
          {error && (
            <div
              role="alert"
              className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-800"
            >
              {error}
            </div>
          )}
          {form && (
            <form className="grid gap-6 sm:grid-cols-2" onSubmit={submit}>
              {form.fields.map((field) => (
                <div
                  key={field.id}
                  className={
                    field.type === "checkbox" || field.type === "textarea"
                      ? "sm:col-span-2"
                      : ""
                  }
                >
                  {field.type !== "checkbox" && (
                    <Label htmlFor={`field-${field.id}`} className="mb-2 block">
                      {text(field.label, locale)}
                      {field.required && (
                        <span className="text-destructive"> *</span>
                      )}
                    </Label>
                  )}
                  <Field field={field} locale={locale} />
                </div>
              ))}
              <div className="sm:col-span-2">
                <Button
                  size="lg"
                  className="w-full sm:w-auto"
                  disabled={submitting}
                >
                  {submitting ? t("common.sending") : t("common.submit")}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
